import type { ISODateTimeWithUTCType } from '@principle-theorem/shared';

/**
 * Types mirror the Ovie Health API wire format (GET /v1/principle/calls).
 * Nullable fields are typed as `| null` because the upstream API returns
 * literal JSON nulls that survive the callable serialisation round-trip.
 */

export enum OvieHealthCallOutcome {
  AppointmentBooked = 'appointment_booked',
  EnquiryResolved = 'enquiry_resolved',
  Transferred = 'transferred',
  Abandoned = 'abandoned',
  EmergencyHandled = 'emergency_handled',
  AppointmentRescheduled = 'appointment_rescheduled',
}

export enum OvieHealthCallerIntent {
  NewBooking = 'new_booking',
  ExistingAppointment = 'existing_appointment',
  AppointmentConfirmation = 'appointment_confirmation',
  GeneralEnquiry = 'general_enquiry',
  Emergency = 'emergency',
  NonDental = 'non_dental',
  Other = 'other',
}

export enum OvieHealthCallerSentiment {
  Positive = 'positive',
  Neutral = 'neutral',
  Frustrated = 'frustrated',
  Distressed = 'distressed',
}

export enum OvieHealthTreatmentCategory {
  Checkup = 'Checkup',
  Cleaning = 'Cleaning',
  Emergency = 'Emergency',
  Restorative = 'Restorative',
  Orthodontic = 'Orthodontic',
  Cosmetic = 'Cosmetic',
  Other = 'Other',
}

export enum OvieHealthCallSuccessful {
  Success = 'success',
  Partial = 'partial',
  Failure = 'failure',
}

export enum OvieHealthTransferReason {
  Emergency = 'emergency',
  BillingPayment = 'billing_payment',
  Cancellation = 'cancellation',
  Reschedule = 'reschedule',
  AiOptout = 'ai_optout',
  SystemFailure = 'system_failure',
  IdentityMismatch = 'identity_mismatch',
  ComplexEnquiry = 'complex_enquiry',
}

export interface IOvieHealthCallRecord {
  id: number;
  callId: string | null;
  callOutcome: OvieHealthCallOutcome | null;
  callSuccessful: OvieHealthCallSuccessful | null;
  appointmentBooked: boolean;
  newPatientCreated: boolean;
  treatmentCategory: OvieHealthTreatmentCategory | null;
  /**
   * Always present. Null when the call was not transferred.
   */
  transferReason: OvieHealthTransferReason | null;
  callerIntent: OvieHealthCallerIntent | null;
  callerSentiment: OvieHealthCallerSentiment | null;
  /**
   * Open enum - the AI model may emit values beyond the documented codes,
   * so this is intentionally a plain string. Null when callerSentiment is
   * null or no root cause was determined.
   */
  sentimentReason: string | null;
  callDurationSeconds: number | null;
  afterHoursCall: boolean;
  repeatCaller: boolean;
  /**
   * 0-23 in clinic local time.
   */
  callHour: number | null;
  /**
   * Masked `+XX***NNNN` by default. Full E.164 requires the
   * `pii:phone_number:read` capability upstream.
   */
  phoneNumber: string | null;
  /**
   * Only present when requested via `include=summary` on the list endpoint.
   * Always included on the single call endpoint.
   */
  summary?: string | null;
  createdAt: ISODateTimeWithUTCType;
}

export interface IOvieHealthCallsListFilters {
  callOutcome?: OvieHealthCallOutcome;
  callerIntent?: OvieHealthCallerIntent;
  callerSentiment?: OvieHealthCallerSentiment;
  treatmentCategory?: OvieHealthTreatmentCategory;
  callSuccessful?: OvieHealthCallSuccessful;
  afterHoursCall?: boolean;
}

export interface IOvieHealthCallsListResponse {
  data: IOvieHealthCallRecord[];
  /**
   * Signed cursor for the next page. Null when exhausted.
   * Back-pagination is not supported by the API.
   */
  nextCursor: string | null;
  /**
   * Number of records in this page only - NOT the total matching count.
   */
  pageCount: number;
}
